import React, { useEffect, useState, useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { useSavedAccounts } from '@/hooks/multipleAccounts/useSavedAccounts';
import { useOrganization } from '@/hooks/useOrganization';
import AccesDenied from './AccessDenied';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { navigateToAuth } from '@/utils/navigationUtils';
import { BoltLoader } from '@/components/ui/bolt-loader';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredRole?: 'admin' | 'manager' | 'cashier';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const location = useLocation();
  const isFastSwitch = location.pathname === '/fast-switch';
  const { loading: authLoading, hasRole, isAuthenticated, refreshSession } = useAuth();
  const { isLoading: orgLoading, currentOrganization } = useOrganization();
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const { translator } = useContext(LanguageProviderContext);
  const { accounts } = useSavedAccounts();
  const fastSwitchGuestAccess = isFastSwitch && accounts.length > 0;
  const skipOrgLoader = !!location.state?.skipOrgLoader;

  // Give initial auth check a maximum of 2 seconds before redirecting
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialCheckDone(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // If auth is not loading and user is not authenticated, redirect immediately
  useEffect(() => {
    if (!authLoading && !isAuthenticated && initialCheckDone) {
      if (fastSwitchGuestAccess) return;
      const redirectTimer = setTimeout(() => {
        navigateToAuth();
      }, 100);

      return () => clearTimeout(redirectTimer);
    }
  }, [authLoading, isAuthenticated, initialCheckDone, fastSwitchGuestAccess]);

  useEffect(() => {
    let timer: number;

    if ((authLoading || orgLoading) && !isAuthenticated) {
      const startTime = Date.now();
      timer = window.setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        setLoadingTime(elapsedSeconds);

        if (elapsedSeconds > 3) {
          setShowRetryButton(true);
        }
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [authLoading, orgLoading, isAuthenticated]);

  const handleRetry = async () => {
    await refreshSession();
    setShowRetryButton(false);
  };
  
  const handleClearAndReload = () => {
    navigateToAuth();
  };

  // Show loading only during initial auth check or if authenticated but org is loading
  // Skip authLoading check on fast-switch to prevent spinner during sign-in attempts
  if (
    (!isFastSwitch && authLoading) ||
    (!isAuthenticated && !initialCheckDone && !fastSwitchGuestAccess)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <BoltLoader size="lg" className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{translator('Global.loading')}</p>

          {loadingTime > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              {translator('Global.elapsedTime')}: {loadingTime}s
            </p>
          )}

          {showRetryButton && (
            <div className="mt-6 space-y-2">
              <p className="text-sm text-muted-foreground mb-2">{translator('Global.moreTimeThanExpected')}</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={handleRetry} variant="outline" size="sm">
                  {translator('Global.retry')}
                </Button>
                <Button onClick={handleClearAndReload} variant="outline" size="sm">
                  {translator('Global.goToLogin')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If not authenticated after initial check, redirect to auth (unless fast-switch recovery)
  if (!isAuthenticated) {
    if (fastSwitchGuestAccess) {
      return <>{children || <Outlet />}</>;
    }
    return <Navigate to="/auth" replace />;
  }

  // If authenticated but organization is still loading
  // Skip orgLoading spinner on fast-switch (handled by FastSwitch component)
  if (isAuthenticated && orgLoading && !isFastSwitch && !skipOrgLoader) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <BoltLoader size="lg" className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{translator('Global.loading')}</p>
        </div>
      </div>
    );
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <AccesDenied />;
  }

  return <>{children || <Outlet />}</>;
};

export default ProtectedRoute;
